import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import Tabs, { SubTab } from "../../components/Tabs";
import ManagePeerReviewGroups from "../peerReviewGroups/ManagePeerReviewGroup";
import { useManageReviewGroupsPageQuery } from "../peerReviewGroups/peerReviewGroupsApiSlice";
import PeerReviewActivityTable from "./coursePeerReviewActivity/PeerReviewActivityTable";
import { useGetCourseReviewsQuery } from "./peerReviewsApiSlice";

enum TeacherPeerReviewsSubTabs {
	ReviewsStatus = "Peer Review Activity",
	DefaultReviewGroups = "Default Peer Review Groups",
}

export default function TeacherPeerReviews() {
	const location = useLocation();
	const [view, setView] = useState<string>("table");
	const [alertMessage, setAlertMessage] = useState("");
	const [alertLevel, setAlertLevel] = useState(AlertLevel.Info);
	const [promptId, setPromptId] = useState<number>();

	useEffect(() => {
		if (location.state != null) {
			setPromptId(location.state.promptId);
		}
	}, []);

	const reviewGroupsResult = useManageReviewGroupsPageQuery();
	const reviewGroupsData = reviewGroupsResult.data;

	const courseReviewsResult = useGetCourseReviewsQuery();
	const courseReviewsData = courseReviewsResult.data;

	const handleAlert = (message: string, level?: AlertLevel) => {
		setAlertMessage(message);
		setAlertLevel(level ?? AlertLevel.Info);
	};

	const initialSubTabs: SubTab[] = useMemo(
		() => [
			{
				name: TeacherPeerReviewsSubTabs.ReviewsStatus,
				current: true,
				content: (
					<PeerReviewActivityTable
						data={courseReviewsData?.activity}
						writerPool={courseReviewsData?.allWriters}
						selectedPromptId={promptId}
					/>
				),
			},
			{
				name: TeacherPeerReviewsSubTabs.DefaultReviewGroups,
				current: false,
				content: <ManagePeerReviewGroups data={reviewGroupsData} setAlert={handleAlert} />,
			},
		],
		[courseReviewsData, reviewGroupsData, promptId]
	);

	return (
		<section className="peerReview">
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Peer Review</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "table", label: "Tables" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64 print:hidden"
				/>
			</div>
			<p className="mt-4">
				Welcome to the Peer Review page! From this page, you can view Peer Review Activity,
				which displays the status of all review requests. You can view all peer reviews that
				have been started or completed when you expand a prompt. In the Default Peer Review
				Groups, you can assign students to peer review groups on a course level.
			</p>
			<AlertMessage
				className="my-4"
				type={alertLevel}
				message={alertMessage}
				showIcon={true}
				onClear={() => handleAlert("")}
			/>
			{view === "table" && <Tabs initialSubTabs={initialSubTabs} />}
			{view === "raw" && (
				<>
					<RtkqResultViewer heading="Review Status" result={courseReviewsResult} />
					<RtkqResultViewer heading="Manage Groups" result={reviewGroupsResult} />
				</>
			)}
		</section>
	);
}
